/*
 * Renders the pair of Box exclusion list editors shared by the add and edit modals, so a
 * folder's exclusions look and validate the same whether they are set at enrollment or later.
 * Purely presentational: each list's state lives in a `useExclusionListDraft` owned by the modal.
 * Each pathname entry is shown as the plain text it matches plus its mode, so a stored glob reads
 * the way the user entered it; stored regexes with no glob form read as advanced and are only
 * removable. Only the pathname list has a match mode; folder IDs are exact values. Pathname entries
 * matching the caller's defaults are folded into a collapsed group below the user's own entries.
 */
import React from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import { DefaultExclusionsGroup } from 'src/components/box-folders/DefaultExclusionsGroup';
import { ExclusionEntryRow, type IndexedExclusionEntry } from 'src/components/box-folders/ExclusionEntryRow';
import {
  getPathnamePlaceholderKey,
  MATCH_MODE_SELECT_WIDTH,
  PathnameMatchModeLabel,
  PathnameMatchModeSelect,
} from 'src/components/box-folders/PathnameMatchModeControl';
import { type ExclusionListDraft } from 'src/hooks/useExclusionListDraft';
import { type PathnameListDraft } from 'src/hooks/usePathnameListDraft';
import { type ExclusionEntry, toPathnameMatch } from 'src/utils/exclusionEntries';
import {
  type ExclusionValidationMessage,
  ExclusionValidationMessages,
  MAX_EXCLUDED_ENTRIES,
} from 'src/utils/exclusionValidation';
import { type PathnameMatch } from 'src/utils/pathnameMatchMode';

/**
 * Leaves the pattern input comfortable room beside the mode dropdown and the Add button once
 * the body's Meridian inset is taken, so a typical path pattern fits without scrolling.
 */
export const EXCLUSIONS_MODAL_WIDTH = 760;

const VALIDATION_MESSAGE_KEYS: Record<ExclusionValidationMessage, string> = {
  [ExclusionValidationMessages.InvalidFolderId]: 'box_folder_exclusions_invalid_folder_id',
  [ExclusionValidationMessages.EmptyPathname]: 'box_folder_exclusions_empty_pattern',
  [ExclusionValidationMessages.PathnameTooLong]: 'box_folder_exclusions_pattern_too_long',
  [ExclusionValidationMessages.WildcardNeedsAdvanced]: 'box_folder_exclusions_wildcard_needs_advanced',
  [ExclusionValidationMessages.Duplicate]: 'box_folder_exclusions_duplicate',
  [ExclusionValidationMessages.ListFull]: 'box_folder_exclusions_list_full',
};

const getValidationMessage = (t: TranslateFn, validation: string): string =>
  t(VALIDATION_MESSAGE_KEYS[validation as ExclusionValidationMessage]);

/** A field placed before the pattern input: its label shares the input label's line, its control the input's. */
interface LeadingField {
  control: React.ReactNode;
  label: React.ReactNode;
  width: number;
}

type RowWidth = number | 'fill' | 'fit';

/** Both rows give the leading field the same width, so its label and control share a left edge with the input's. */
const getFieldWidths = (leadingField: LeadingField | undefined, inputWidths: RowWidth[]): RowWidth[] =>
  leadingField === undefined ? inputWidths : [leadingField.width, ...inputWidths];

interface ExclusionEntryComposerProps {
  addLabel: string;
  draft: ExclusionListDraft;
  inputHelperText?: string;
  inputLabel: string;
  inputPlaceholder: string;
  isDisabled: boolean;
  leadingField?: LeadingField;
  testIdPrefix: string;
}

/**
 * Labels sit on their own line above the controls, rather than inside Meridian's floating
 * label, so the leading field's help icon reads as part of its label and every control
 * shares one top edge even when the input shows helper or error text beneath it.
 */
const ExclusionEntryComposer: React.FC<ExclusionEntryComposerProps> = ({
  addLabel,
  draft,
  inputHelperText,
  inputLabel,
  inputPlaceholder,
  isDisabled,
  leadingField,
  testIdPrefix,
}) => {
  const { t } = useTranslation();
  const inputId = `${testIdPrefix}-input`;
  const isAtLimit = draft.entries.length >= MAX_EXCLUDED_ENTRIES;
  const validationError = draft.validation === null ? null : getValidationMessage(t, draft.validation);

  return (
    <Column spacing="100">
      <Row alignmentVertical="center" spacing="300" widths={getFieldWidths(leadingField, ['fill'])}>
        {leadingField?.label}
        <Text htmlFor={inputId} tag="label" type="b200">{inputLabel}</Text>
      </Row>
      <Row alignmentVertical="top" spacing="300" widths={getFieldWidths(leadingField, ['fill', 'fit'])}>
        {leadingField?.control}
        <Input
          // Meridian draws helperText above a medium input, which pushed the box below the Add
          // button; constraintText is drawn beneath it, so the controls keep one top edge.
          constraintText={inputHelperText}
          data-testid={`${testIdPrefix}-input`}
          disabled={isDisabled}
          error={validationError !== null}
          errorMessage={validationError ?? undefined}
          id={inputId}
          placeholder={inputPlaceholder}
          value={draft.input}
          onChange={draft.setInput}
        />
        <Button
          data-testid={`${testIdPrefix}-add-button`}
          disabled={isDisabled || isAtLimit}
          type="secondary"
          onClick={draft.add}
        >
          {addLabel}
        </Button>
      </Row>
    </Column>
  );
};

interface ExclusionListEditorProps extends ExclusionEntryComposerProps {
  /** Entries with one of these values render in the collapsed defaults group instead of the main list. */
  defaultEntryValues?: ReadonlySet<string>;
  /** Describes each entry's match for display; lists without match modes omit it. */
  getEntryMatch?: (entry: ExclusionEntry) => PathnameMatch;
}

const ExclusionListEditor: React.FC<ExclusionListEditorProps> = ({
  defaultEntryValues,
  getEntryMatch,
  ...composerProps
}) => {
  const { draft, isDisabled, testIdPrefix } = composerProps;
  const indexedEntries: IndexedExclusionEntry[] = draft.entries.map((entry, index) => ({ entry, index }));
  const isDefaultEntry = ({ entry }: IndexedExclusionEntry): boolean => defaultEntryValues?.has(entry.value) ?? false;

  return (
    <Column data-testid={`${testIdPrefix}-editor`} spacing="200">
      <ExclusionEntryComposer {...composerProps} />

      {indexedEntries.filter((item) => !isDefaultEntry(item)).map(({ entry, index }) => (
        <ExclusionEntryRow
          entry={entry}
          index={index}
          isDisabled={isDisabled}
          key={`${entry.value}-${index}`}
          match={getEntryMatch?.(entry)}
          testIdPrefix={testIdPrefix}
          onRemove={() => { draft.remove(index); }}
        />
      ))}

      <DefaultExclusionsGroup
        entries={indexedEntries.filter(isDefaultEntry)}
        getEntryMatch={getEntryMatch}
        isDisabled={isDisabled}
        testIdPrefix={testIdPrefix}
        onRemove={draft.remove}
      />
    </Column>
  );
};

interface BoxFolderExclusionsEditorProps {
  /** Pathname entries equal to one of these are grouped as auto-added defaults; the draft is unchanged. */
  defaultPathnames?: readonly ExclusionEntry[];
  folderIds: ExclusionListDraft;
  isDisabled: boolean;
  pathnames: PathnameListDraft;
  /** Each list's test ids are `${testIdPrefix}-folder-ids-*` and `${testIdPrefix}-pathnames-*`. */
  testIdPrefix: string;
}

export const BoxFolderExclusionsEditor: React.FC<BoxFolderExclusionsEditorProps> = ({
  defaultPathnames = [],
  folderIds,
  isDisabled,
  pathnames,
  testIdPrefix,
}) => {
  const { t } = useTranslation();
  const pathnamesTestIdPrefix = `${testIdPrefix}-pathnames`;
  const defaultPathnameValues = new Set(defaultPathnames.map(({ value }) => value));

  return (
    <Column spacing="450">
      <ExclusionListEditor
        addLabel={t('box_folder_exclusions_add_folder')}
        draft={folderIds}
        inputHelperText={t('box_folder_exclusions_folder_id_helper')}
        inputLabel={t('box_folder_exclusions_folder_ids_label')}
        inputPlaceholder={t('box_folder_exclusions_folder_id_placeholder')}
        isDisabled={isDisabled}
        testIdPrefix={`${testIdPrefix}-folder-ids`}
      />
      <ExclusionListEditor
        addLabel={t('box_folder_exclusions_add_pattern')}
        defaultEntryValues={defaultPathnameValues}
        draft={pathnames}
        getEntryMatch={toPathnameMatch}
        inputLabel={t('box_folder_exclusions_pathnames_label')}
        inputPlaceholder={t(getPathnamePlaceholderKey(pathnames.mode))}
        isDisabled={isDisabled}
        leadingField={{
          control: (
            <PathnameMatchModeSelect draft={pathnames} isDisabled={isDisabled} testIdPrefix={pathnamesTestIdPrefix} />
          ),
          label: <PathnameMatchModeLabel testIdPrefix={pathnamesTestIdPrefix} />,
          width: MATCH_MODE_SELECT_WIDTH,
        }}
        testIdPrefix={pathnamesTestIdPrefix}
      />
    </Column>
  );
};
